import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

// Comportamiento común de los autocompletar (proveedor, producto, tipo de gasto, beneficiario),
// pensado sobre todo para celular:
//  - tocar fuera del campo o de la lista, o desplazar la pantalla, cierra las sugerencias sin borrar lo escrito;
//  - tocar el campo otra vez las vuelve a mostrar según el texto actual;
//  - Escape o la ✕ del listado las cierran (sin borrar lo escrito);
//  - el listado muestra hasta 10 coincidencias; si hay más, se desliza con el dedo (sin barra visible);
//  - el botón × (BotonLimpiar) vacía el campo, cierra la lista y deja el cursor listo para escribir.
export function useAutocompletar() {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const sinAbrir = useRef(false); // el foco que devuelve "limpiar" no debe reabrir la lista
  const abiertoEn = useRef(0);
  const listId = useId();

  useEffect(() => { if (abierto) abiertoEn.current = Date.now(); }, [abierto]);
  useEffect(() => {
    const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    // Al abrir el teclado el celular desplaza la pantalla: se ignora ese primer desplazamiento.
    const desplazar = (e) => {
      if (Date.now() - abiertoEn.current < 600 || (ref.current && e.target instanceof Node && ref.current.contains(e.target))) return;
      setAbierto(false);
    };
    // pointerdown cubre mouse y toque (en celular mousedown llega tarde o no llega).
    document.addEventListener('pointerdown', fuera);
    window.addEventListener('scroll', desplazar, true);
    return () => { document.removeEventListener('pointerdown', fuera); window.removeEventListener('scroll', desplazar, true); };
  }, []);

  const abrir = useCallback(() => { if (sinAbrir.current) { sinAbrir.current = false; return; } setAbierto(true); }, []);
  /** Props para el input: abre al enfocar o tocar, cierra con Escape. */
  const propsInput = {
    ref: inputRef, onFocus: abrir, onClick: () => setAbierto(true),
    onKeyDown: (e) => { if (e.key === 'Escape') setAbierto(false); },
    autoComplete: 'off', role: 'combobox', 'aria-controls': listId, 'aria-expanded': abierto ? 'true' : 'false', 'aria-autocomplete': 'list',
  };
  /** Vacía el campo (con onVaciar, que también limpia el ID), cierra la lista y devuelve el cursor al campo. */
  const limpiar = useCallback((onVaciar) => {
    onVaciar();
    setAbierto(false);
    sinAbrir.current = true;
    inputRef.current?.focus();
    // Si el input ya tenía el foco no llega onFocus: se suelta la marca para el siguiente toque.
    setTimeout(() => { sinAbrir.current = false; }, 0);
  }, []);
  /** ✕ del listado: SOLO cierra las sugerencias (no borra el texto ni el ID elegido). La × del input es la que limpia. */
  const botonCerrar = (
    <li className="auto-cerrar" role="presentation">
      <button type="button" aria-label="Cerrar sugerencias" title="Cerrar sugerencias"
        onPointerDown={(e) => e.preventDefault()} onClick={() => setAbierto(false)}>✕</button>
    </li>
  );
  return { abierto, setAbierto, ref, listId, propsInput, limpiar, botonCerrar };
}

/** Botón × dentro del input. Solo aparece con texto; área de toque amplia para celular. */
export function BotonLimpiar({ visible, onClick, etiqueta = 'Limpiar' }) {
  if (!visible) return null;
  return (
    <button type="button" className="auto-limpiar" aria-label={etiqueta} title={etiqueta}
      // preventDefault: el toque no quita el foco del input (evita que el teclado del celular se cierre y abra).
      onPointerDown={(e) => e.preventDefault()} onClick={onClick}>×</button>
  );
}
